import { motion, useReducedMotion } from "framer-motion";
import { type ReactNode } from "react";

/**
 * Wrap a "reward" element (streak count, score number, achievement badge)
 * with this. Triggers a spring-based scale + fade pulse the first time the
 * element mounts. Respects prefers-reduced-motion.
 *
 * Use sparingly -- per FRONTEND_PLAN, expressive moments are for specific
 * win states, NOT every render.
 */
export function RewardMoment({
  children,
  delay = 0,
  intensity = "normal",
}: {
  children: ReactNode;
  delay?: number;
  intensity?: "subtle" | "normal" | "strong";
}) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;

  const scaleIn = intensity === "strong" ? 1.18 : intensity === "subtle" ? 1.05 : 1.1;

  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: [0.6, scaleIn, 1] }}
      transition={{
        duration: 0.6,
        delay,
        times: [0, 0.6, 1],
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{ display: "inline-block" }}
    >
      {children}
    </motion.span>
  );
}

/**
 * Subtle pulse animation for a sustained element -- e.g. the streak chip
 * pulsing once per session to draw the eye. Triggers ONCE per mount.
 */
export function PulseOnce({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;

  return (
    <motion.span
      animate={{
        scale: [1, 1.06, 1],
      }}
      transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.4,
      }}
      style={{ display: "inline-block" }}
    >
      {children}
    </motion.span>
  );
}
